import type { ProviderFilter } from '@/shared/store/create-appointment-handoff';
import type { ReferralRow, ReferralStatus } from '@/types/referral-api';

/** Pager sizes (PROD-1395). The default is 25. */
export const REFERRAL_PAGE_SIZES = [10, 25, 50] as const;
export type ReferralPageSize = (typeof REFERRAL_PAGE_SIZES)[number];
export const REFERRAL_DEFAULT_PAGE_SIZE: ReferralPageSize = 25;

/** The Status filter. "Open" is what the front desk still has to act on. */
export const REFERRAL_STATUS_FILTERS = [
  { value: 'open', label: 'Open', statuses: ['sent', 'accepted'] },
  { value: 'sent', label: 'Sent', statuses: ['sent'] },
  { value: 'accepted', label: 'Accepted', statuses: ['accepted'] },
  { value: 'booked', label: 'Booked', statuses: ['booked'] },
] as const satisfies readonly { value: string; label: string; statuses: readonly ReferralStatus[] }[];
export type ReferralStatusFilter = (typeof REFERRAL_STATUS_FILTERS)[number]['value'];

/** Every performer role the list can filter by. Add a role here and the filter and the table both know it. */
export const REFERRAL_ROLES = [
  { value: 'dietitian', label: 'Dietitian', discipline: 'dietetics' },
] as const satisfies readonly { value: string; label: string; discipline: ProviderFilter['discipline'] }[];

/** Value of the "All roles" option. It sends no performerRole. */
export const ALL_ROLES = 'all';

export function statusesFor(filter: ReferralStatusFilter): readonly ReferralStatus[] {
  return REFERRAL_STATUS_FILTERS.find((f) => f.value === filter)?.statuses ?? ['sent', 'accepted'];
}

/** "dietitian" -> "Dietitian". An unknown role is shown with a capital letter. */
export function roleLabel(performerRole: string | null | undefined): string {
  const role = performerRole ?? '';
  const known = REFERRAL_ROLES.find((r) => r.value === role.trim().toLowerCase());
  if (known) return known.label;
  const trimmed = role.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() + trimmed.slice(1) : '—';
}

/** The provider list filter for a referral's role. An unknown role gives none (the full list, as before). */
export function providerFilterFor(row: Pick<ReferralRow, 'performerRole' | 'targetPractitionerId'>): ProviderFilter | undefined {
  const role = REFERRAL_ROLES.find((r) => r.value === (row.performerRole ?? '').trim().toLowerCase());
  if (!role) return undefined;
  return { discipline: role.discipline, label: role.label, preferredProviderId: row.targetPractitionerId?.trim() || null };
}

/** targetDisplay when it names someone, not just the role again. */
export function targetPerson(row: Pick<ReferralRow, 'performerRole' | 'targetDisplay'>): string | null {
  const target = row.targetDisplay?.trim();
  if (!target) return null;
  const lower = target.toLowerCase();
  if (lower === (row.performerRole ?? '').trim().toLowerCase() || lower === roleLabel(row.performerRole).toLowerCase()) {
    return null;
  }
  return target;
}

/** A referral can be booked while the front desk still owes it an appointment. */
export function canBook(status: ReferralStatus | null | undefined): boolean {
  return status === 'sent' || status === 'accepted';
}
